import { useMemo } from 'react';
import http from 'hub-http/clients/apiClient';
import { registerQuery, useQuery } from 'data-fetching-client';
import { USER_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { captureException } from '../utils/sentryUtils';
import type {
  AppHubUserRecord,
  CrmSearchResponse,
} from '../types/CrmSearchUser';

const APP_HUB_USERS_FIELD = 'usersData';

const USER_PROPERTIES = [
  'hs_email',
  'hs_given_name',
  'hs_family_name',
  'hs_internal_user_id',
  'hubspot_owner_id',
];

const FETCH_APP_HUB_USERS = registerQuery<
  typeof APP_HUB_USERS_FIELD,
  { filterProperty: string; filterValues: number[] },
  CrmSearchResponse
>({
  fieldName: APP_HUB_USERS_FIELD,
  args: ['filterProperty', 'filterValues'],
  fetcher: ({ filterProperty, filterValues }) =>
    http.post('crm-search/search', {
      data: {
        count: filterValues.length,
        objectTypeId: USER_TYPE_ID,
        requestOptions: {
          properties: USER_PROPERTIES,
        },
        filterGroups: [
          {
            filters: [
              {
                property: filterProperty,
                operator: 'IN',
                values: filterValues,
              },
            ],
          },
        ],
      },
    }),
});

type UseAppHubUsersByUserIds = { userIds: number[]; ownerIds?: never };
type UseAppHubUsersByOwnerIds = { ownerIds: number[]; userIds?: never };
export type UseAppHubUsersOptions =
  | UseAppHubUsersByUserIds
  | UseAppHubUsersByOwnerIds;

export const useAppHubUsers = ({
  userIds,
  ownerIds,
}: UseAppHubUsersOptions) => {
  const isOwnerIdsFilter = (ownerIds || []).length > 0;
  const isUserIdsFilter = (userIds || []).length > 0;
  const filterProperty = isOwnerIdsFilter
    ? 'hubspot_owner_id'
    : 'hs_internal_user_id';
  const filterValues = isOwnerIdsFilter ? ownerIds! : userIds!;

  const { data, loading, error } = useQuery(FETCH_APP_HUB_USERS, {
    variables: { filterProperty, filterValues },
    skip: !isUserIdsFilter && !isOwnerIdsFilter,
    onError: (err: Error) => {
      captureException(err, {
        extra: {
          fingerprint: 'useAppHubUsers-crmSearch',
          filterProperty,
          filterValues,
        },
      });
    },
  });

  const usersRecord = useMemo(() => {
    const record: AppHubUserRecord = {};
    const results = data?.[APP_HUB_USERS_FIELD]?.results ?? [];
    const keyProperty = isOwnerIdsFilter
      ? 'hubspot_owner_id'
      : 'hs_internal_user_id';
    for (const result of results) {
      const key = Number(result.properties[keyProperty]?.value);
      if (!isNaN(key)) {
        record[key] = {
          email: result.properties.hs_email?.value ?? '',
          firstName: result.properties.hs_given_name?.value ?? '',
          lastName: result.properties.hs_family_name?.value ?? '',
          ownerId: result.properties.hubspot_owner_id?.value ?? '',
        };
      }
    }
    return record;
  }, [data, isOwnerIdsFilter]);

  return { usersRecord, loading, error };
};
